import { Injectable } from '@angular/core';
import { Auth, authState, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, User as FirebaseAuthUser, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from '@angular/fire/auth';
import { Database, ref, objectVal, set } from '@angular/fire/database';
import { BehaviorSubject, Observable, of, firstValueFrom } from 'rxjs';
import { switchMap, filter, tap, map } from 'rxjs/operators';
import { User } from '../shared/models/user.interface';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null | undefined>(undefined);
  public currentUser$ = this.currentUserSubject.asObservable().pipe(filter(user => user !== undefined));
  private isInitialized = false;

  constructor(private auth: Auth, private db: Database) {
    authState(this.auth).pipe(
      switchMap(firebaseUser => {
        if (firebaseUser) {
          const userDocRef = ref(this.db, `users/${firebaseUser.uid}`);
          return objectVal<User>(userDocRef, { keyField: "uid" });
        } else {
          return of(null); // User is logged out
        }
      }),
      tap(user => {
        // This ensures we only emit a user object once it's fully loaded with a role.
        if (user === null || (user && user.role)) {
          this.currentUserSubject.next(user);
          this.isInitialized = true;
        }
      })
    ).subscribe();
  }

  login(email: string, password: string) {
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  logout() {
    this.currentUserSubject.next(null);
    return signOut(this.auth);
  }

  waitForUser(): Promise<User | null> {
    return firstValueFrom(this.currentUser$.pipe(filter((user): user is User | null => user !== undefined)));
  }

  async register(email: string, password: string): Promise<void> {
    const credential = await createUserWithEmailAndPassword(this.auth, email, password);
    const userDocRef = ref(this.db, `users/${credential.user.uid}`);
    await set(userDocRef, {
      uid: credential.user.uid,
      email: credential.user.email,
      role: 'Technician' // Default role
    });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user || !user.email) {
      throw new Error('No user is currently signed in or user has no email.');
    }

    // Create a credential with the user's email and current password
    const credential = EmailAuthProvider.credential(user.email, currentPassword);

    // Re-authenticate the user
    await reauthenticateWithCredential(user, credential);

    // If re-authentication is successful, update the password
    await updatePassword(user, newPassword);
  }

  hasRole(user: User | null, role: 'Administrator' | 'Coordinator' | 'Technician'): boolean {
    return !!user && user.role === role;
  }

  getUserRole(): Observable<'Administrator' | 'Coordinator' | 'Technician' | null> {
    return this.currentUser$.pipe(
      map(user => user ? user.role : null)
    );
  }
}
